/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { EmbeddedAppConfig } from '@/types/embeddedApp';

/**
 * Configuration for the Telemetry Beta embedded app.
 * Hosted at telemetry-dashboard.{env}.mimic.com
 */
const TELEMETRY_URL_PARAM_KEYS = ['tenant', 'subtenantId', 'nodeIds', 'embedMode'] as const;

function appendTelemetryQuery(base: string, params?: Record<string, string>): string {
  if (!params) return base;
  const url = new URL(base);

  TELEMETRY_URL_PARAM_KEYS.forEach((key) => {
    const value = params[key];
    if (value) url.searchParams.set(key, value);
  });

  return url.toString();
}

export const TELEMETRY_BETA_CONFIG: EmbeddedAppConfig = {
  name: 'Telemetry Beta',

  getUrl: (env: string, params?: Record<string, string>) => {
    const adminMode = params?.adminMode === 'true';
    const path = params?.path ? `/${params.path.replace(/^\//, '')}` : undefined;

    const host =
      env === 'local' || env === 'localhost' ? 'http://localhost:5179' : `https://telemetry-dashboard.${env}.mimic.com`;

    // The legacy histogram + events view (which is what dashboard embeds as
    // "Telemetry Viewer") now lives at /telemetry-viewer in the viewer app.
    // "/" on that host serves the new standalone Activity shell.
    let base: string;
    if (path) base = `${host}${path}`;
    else if (adminMode) base = `${host}/telemetry-viewer/admin`;
    else base = `${host}/telemetry-viewer`;

    return appendTelemetryQuery(base, params);
  },

  getAllowedOrigins: (env: string) => {
    if (env === 'local' || env === 'localhost') {
      return ['http://localhost:5179'];
    }
    return [`https://telemetry-dashboard.${env}.mimic.com`];
  },
};

/**
 * Configuration for the Node E2E VM Management embedded app.
 * Hosted at localhost:3000 (local) or mimic.ngrok.io (qa/dev).
 */
export const NODE_E2E_CONFIG: EmbeddedAppConfig = {
  name: 'Node E2E',

  getUrl: (env: string) => {
    if (env === 'local' || env === 'localhost') {
      return 'http://localhost:3000/node-e2e';
    }
    if (env === 'qa' || env === 'dev') {
      return 'https://mimic.ngrok.io/node-e2e';
    }
    return '';
  },

  getAllowedOrigins: (env: string) => {
    if (env === 'local' || env === 'localhost') {
      return ['http://localhost:3000'];
    }
    return ['https://mimic.ngrok.io'];
  },
};
